import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  Typography,
} from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  CampaignOutreachDraft,
  CampaignOutreachDraftGenerationStatus,
  CampaignOutreachDraftReviewStatus,
} from "schemas/dashboard";

import Editor from "components/Editor";
import { extensions } from "components/Editor/utils";
import { PersonalizeToneOption } from "features/Influencer/QuickSendIndividualDrawer/Dialogs/PersonalizeDialog/schema";
import { fetcherAuth } from "utils/api";
import { useRefreshChatCredits } from "utils/useRefreshChatCredits";

import StepNav from "../../StepNav";
import builderStyles from "../../styles";
import RegenerateDialog from "./RegenerateDialog";
import styles from "./styles";

interface Props {
  campaignId: number;
  brandCount: number;
  onBack: () => void;
  onContinue: () => void;
  onRetryFailed: () => void;
}

function statusMeta(draft: CampaignOutreachDraft): {
  color: string;
  icon: string;
} {
  if (draft.generationStatus === CampaignOutreachDraftGenerationStatus.failed) {
    return { color: "#C0392B", icon: "fa-solid fa-circle-exclamation" };
  }
  if (draft.reviewStatus === CampaignOutreachDraftReviewStatus.approved) {
    return { color: "#165C4E", icon: "fa-solid fa-circle-check" };
  }
  if (draft.isEdited) return { color: "#b9a311", icon: "fa-solid fa-pen" };
  return { color: "#8084FF", icon: "fa-solid fa-circle-half-stroke" };
}

const MARK_OPEN =
  '<mark style="background:rgba(177,179,248,0.30);border-radius:4px;padding:1px 3px">';

// Wrap highlight spans via string replace (best-effort; body may be HTML).
function highlightHtml(text: string, spans: string[]): string {
  return (spans ?? [])
    .filter(Boolean)
    .reduce(
      (acc, span) => acc.split(span).join(`${MARK_OPEN}${span}</mark>`),
      text,
    );
}

export default function Review({
  campaignId,
  brandCount,
  onBack,
  onContinue,
  onRetryFailed,
}: Props) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { subscription } = useContext(SubscriptionContext);
  const refreshCredits = useRefreshChatCredits();

  const [drafts, setDrafts] = useState<CampaignOutreachDraft[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);
  const bodyEditor = useEditor({
    extensions: extensions({ placeholderText: "Write your pitch…" }),
    content: "",
  }) as TipTapEditor;
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [continuing, setContinuing] = useState(false);

  const baseUrl = `/api/organization/${currentOrg?.id}/campaigns/${campaignId}/outreach-drafts`;
  const selected = drafts.find((d) => d.id === selectedId) ?? null;
  const approvedCount = drafts.filter(
    (d) => d.reviewStatus === CampaignOutreachDraftReviewStatus.approved,
  ).length;
  const failedCount = drafts.filter(
    (d) => d.generationStatus === CampaignOutreachDraftGenerationStatus.failed,
  ).length;
  const approvableCount = drafts.length - failedCount;
  const allApproved = drafts.length > 0 && approvedCount >= approvableCount;
  const retryableFailedCount = drafts.filter(
    (d) =>
      d.generationStatus === CampaignOutreachDraftGenerationStatus.failed &&
      !d.noContact,
  ).length;

  const replaceDraft = (updated: CampaignOutreachDraft) =>
    setDrafts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));

  useEffect(() => {
    const load = async () => {
      if (!currentOrg?.id) return;
      try {
        const { outreachDrafts } = await fetcherAuth(getToken, baseUrl, "GET");
        setDrafts(outreachDrafts ?? []);
        if (outreachDrafts?.length) setSelectedId(outreachDrafts[0].id);
      } catch (error) {
        setErrorAlert(error);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const selectDraft = (draft: CampaignOutreachDraft) => {
    setSelectedId(draft.id);
    setEditing(false);
  };

  const startEditing = () => {
    if (!selected) return;
    setEditSubject(selected.subject ?? "");
    bodyEditor?.commands.setContent(selected.body ?? "");
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!selected) return;
    const body = bodyEditor?.isEmpty ? "" : bodyEditor?.getHTML() ?? "";
    try {
      const updated = await fetcherAuth(
        getToken,
        `${baseUrl}/${selected.id}`,
        "PATCH",
        {},
        { subject: editSubject, body },
      );
      replaceDraft(updated);
      setEditing(false);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const goToNextUnapproved = (justApprovedId: number) => {
    const idx = drafts.findIndex((d) => d.id === justApprovedId);
    if (idx === -1) return;
    const next = [...drafts.slice(idx + 1), ...drafts.slice(0, idx)].find(
      (d) => d.reviewStatus !== CampaignOutreachDraftReviewStatus.approved,
    );
    if (next) {
      setSelectedId(next.id);
      setEditing(false);
    }
  };

  const setApproval = async (
    draft: CampaignOutreachDraft,
    approve: boolean,
  ) => {
    try {
      const updated = await fetcherAuth(
        getToken,
        `${baseUrl}/${draft.id}/${approve ? "approve" : "unapprove"}`,
        "POST",
        {},
        {},
      );
      replaceDraft(updated);
      if (approve) goToNextUnapproved(draft.id);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const regenerate = async (
    draft: CampaignOutreachDraft,
    tone: PersonalizeToneOption | null,
    notes: string,
  ) => {
    setRegenDialogOpen(false);
    setRegeneratingId(draft.id);
    try {
      const updated = await fetcherAuth(
        getToken,
        `${baseUrl}/${draft.id}/regenerate`,
        "POST",
        {},
        { tone, notes },
      );
      replaceDraft(updated);
      refreshCredits();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setRegeneratingId(null);
    }
  };

  const approveAllAndContinue = async () => {
    setConfirmOpen(false);
    setContinuing(true);
    try {
      await fetcherAuth(getToken, `${baseUrl}/approve-all`, "POST", {}, {});
      onContinue();
    } catch (error) {
      setErrorAlert(error);
      setContinuing(false);
    }
  };

  const handleContinueClick = () => {
    if (!allApproved) {
      setConfirmOpen(true);
      return;
    }
    approveAllAndContinue();
  };

  const isApproved =
    selected?.reviewStatus === CampaignOutreachDraftReviewStatus.approved;
  const isFailed =
    selected?.generationStatus === CampaignOutreachDraftGenerationStatus.failed;
  const isNoContact = Boolean(selected?.noContact);
  const contactLabel =
    selected?.contactName || (isNoContact ? "No contact found" : "—");

  return (
    <Box sx={builderStyles.formColumn}>
      <Box sx={styles.revGrid}>
        <Box sx={styles.revList}>
          <Box sx={styles.revListHead}>
            <strong style={{ color: "#151e16" }}>
              {approvedCount}/{approvableCount || brandCount}
            </strong>{" "}
            pitches approved
            {failedCount > 0 && (
              <Box component="span" sx={styles.headFailed}>
                {" · "}
                {failedCount} failed
              </Box>
            )}
          </Box>
          <Box sx={styles.revScroll}>
            {drafts.map((draft) => (
              <Box
                key={draft.id}
                sx={styles.revItem(draft.id === selectedId)}
                onClick={() => selectDraft(draft)}
              >
                <Avatar sx={styles.bubble}>
                  {(draft.bentoBrand?.name ?? "?").charAt(0)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={styles.revName}>
                    {draft.bentoBrand?.name ?? "Brand"}
                  </Typography>
                  <Typography sx={styles.revSub}>
                    {draft.bentoBrand?.instagramUsername
                      ? `@${draft.bentoBrand.instagramUsername}`
                      : draft.bentoBrand?.website}
                  </Typography>
                </Box>
                <Box
                  component="i"
                  className={statusMeta(draft).icon}
                  sx={styles.statusIcon(statusMeta(draft).color)}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={styles.revDetail}>
          {selected && (
            <>
              <Box sx={styles.revDetailHead}>
                <Avatar sx={{ ...styles.bubble, width: 44, height: 44 }}>
                  {(selected.bentoBrand?.name ?? "?").charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={styles.revDetailName}>
                    {selected.bentoBrand?.name}
                  </Typography>
                  <Typography sx={styles.revContact}>
                    {contactLabel}
                    {selected.contactTitle ? ` · ${selected.contactTitle}` : ""}
                  </Typography>
                </Box>
                {isApproved ? (
                  <Box sx={styles.approvedFlag}>
                    <Box component="i" className="fa-solid fa-circle-check" />
                    {selected.isEdited ? "Edited · approved" : "Approved"}
                  </Box>
                ) : selected.isEdited ? (
                  <Box sx={styles.pill("draft")}>
                    <Box component="i" className="fa-solid fa-pen" /> Edited ·
                    not approved
                  </Box>
                ) : (
                  <Box sx={styles.pill("scheduled")}>
                    <Box component="i" className="fa-solid fa-sparkles" /> AI
                    draft
                  </Box>
                )}
              </Box>

              <Box sx={styles.revDetailBody}>
                {selected.bentoBrand && (
                  <Box sx={styles.persBar}>
                    <Box sx={styles.aiSourcesIcon}>
                      <Box component="i" className="fa-solid fa-sparkles" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={styles.persBarTitle}>
                        Personalized for {selected.bentoBrand.name} using:
                      </Typography>
                      {(selected.bentoBrand.instagramUsername ||
                        selected.bentoBrand.website) && (
                        <Box sx={styles.persBarMeta}>
                          {selected.bentoBrand.instagramUsername && (
                            <Box component="span" sx={styles.persBarMetaItem}>
                              <Box
                                component="i"
                                className="fa-brands fa-instagram"
                              />
                              @{selected.bentoBrand.instagramUsername}
                            </Box>
                          )}
                          {selected.bentoBrand.website && (
                            <Box component="span" sx={styles.persBarMetaItem}>
                              <Box
                                component="i"
                                className="fa-solid fa-globe"
                              />
                              {selected.bentoBrand.website}
                            </Box>
                          )}
                        </Box>
                      )}
                      {selected.bentoBrand.description && (
                        <Typography sx={styles.persBarText}>
                          {selected.bentoBrand.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {isFailed && (
                  <Box sx={styles.regenError}>
                    <Box
                      component="i"
                      className="fa-solid fa-circle-exclamation"
                      sx={styles.regenErrorIcon}
                    />
                    <Box sx={{ flex: 1 }}>
                      {selected.errorMessage ||
                        "Couldn't generate this pitch. Give it another try."}
                    </Box>
                    {!isNoContact && (
                      <Button
                        size="small"
                        sx={{ textTransform: "none" }}
                        onClick={() => regenerate(selected, null, "")}
                      >
                        Try again
                      </Button>
                    )}
                  </Box>
                )}

                <Box sx={styles.email}>
                  {regeneratingId === selected.id && (
                    <Box sx={styles.regenOverlay}>
                      <CircularProgress size={26} />
                      Regenerating pitch for {selected.bentoBrand?.name}…
                    </Box>
                  )}
                  <Box sx={styles.emailRow}>
                    <Box component="span" sx={styles.emailLabel}>
                      To
                    </Box>
                    <Box component="span" sx={styles.emailVal}>
                      {contactLabel}
                    </Box>
                  </Box>
                  <Box sx={styles.emailRow}>
                    <Box component="span" sx={styles.emailLabel}>
                      Subject
                    </Box>
                    {editing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editSubject}
                        onChange={(e) => setEditSubject(e.target.value)}
                      />
                    ) : (
                      <Box
                        component="span"
                        sx={styles.emailSubjectVal}
                        dangerouslySetInnerHTML={{
                          __html: highlightHtml(
                            selected.subject ?? "",
                            selected.personalizedSpans,
                          ),
                        }}
                      />
                    )}
                  </Box>
                  {editing ? (
                    <Box sx={styles.emailBodyEditor}>
                      <Editor editor={bodyEditor} />
                    </Box>
                  ) : (
                    <Box
                      sx={styles.emailBody}
                      dangerouslySetInnerHTML={{
                        __html: highlightHtml(
                          selected.body ?? "",
                          selected.personalizedSpans,
                        ),
                      }}
                    />
                  )}
                </Box>

                {!editing && selected.personalizedSpans?.length > 0 && (
                  <Box sx={styles.caveat}>
                    <Box component="span" sx={styles.caveatSwatch} />
                    <span>
                      <strong>Highlighted text was personalized by AI.</strong>{" "}
                      AI can get details wrong — give each pitch a quick read
                      and edit anything that&apos;s off before it sends.
                    </span>
                  </Box>
                )}
              </Box>

              <Box sx={styles.detailFoot}>
                {editing ? (
                  <>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      onClick={() => setEditing(false)}
                      sx={{ textTransform: "none" }}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={saveEdit}
                      sx={{ textTransform: "none" }}
                    >
                      Save changes
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      disabled={isNoContact || regeneratingId === selected.id}
                      onClick={startEditing}
                      sx={{ textTransform: "none" }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-pen-to-square"
                        sx={{ mr: 0.5 }}
                      />
                      Edit
                    </Button>
                    <Button
                      size="small"
                      disabled={isNoContact || regeneratingId === selected.id}
                      onClick={() => setRegenDialogOpen(true)}
                      sx={{ textTransform: "none" }}
                    >
                      <Box
                        component="i"
                        className="fa-solid fa-arrows-rotate"
                        sx={{ mr: 0.5 }}
                      />
                      Regenerate
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {isApproved ? (
                      <Button
                        size="small"
                        onClick={() => setApproval(selected, false)}
                        sx={{ textTransform: "none" }}
                      >
                        Unapprove
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={isFailed || regeneratingId === selected.id}
                        onClick={() => setApproval(selected, true)}
                        sx={{ textTransform: "none" }}
                      >
                        <Box
                          component="i"
                          className="fa-solid fa-check"
                          sx={{ mr: 0.5 }}
                        />
                        Approve pitch
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <StepNav
        onBack={onBack}
        onNext={handleContinueClick}
        nextLabel={allApproved ? "Continue" : "Approve all & continue"}
        nextIcon="fa-solid fa-check"
        nextDisabled={approvableCount === 0}
        nextLoading={continuing}
        secondaryAction={
          <Box sx={styles.footActions}>
            <Typography sx={styles.footInfo}>
              {approvableCount === 0 ? (
                "No pitches generated yet — retry to continue"
              ) : (
                <>
                  <strong style={{ color: "#151e16" }}>{approvedCount}</strong>{" "}
                  of {approvableCount} approved
                  {failedCount > 0 && `, ${failedCount} failed`}
                </>
              )}
            </Typography>
            {retryableFailedCount > 0 && (
              <Button
                size="small"
                onClick={onRetryFailed}
                sx={styles.retryFailedButton}
              >
                <Box
                  component="i"
                  className="fa-solid fa-arrows-rotate"
                  sx={{ mr: 0.5, fontSize: 12 }}
                />
                Retry {retryableFailedCount} failed
              </Button>
            )}
          </Box>
        }
      />

      {selected && (
        <RegenerateDialog
          open={regenDialogOpen}
          brandName={selected.bentoBrand?.name ?? "this brand"}
          creditsLeft={subscription?.remainingChatMessages ?? 0}
          loading={regeneratingId === selected.id}
          onClose={() => setRegenDialogOpen(false)}
          onSubmit={(tone, notes) => regenerate(selected, tone, notes)}
        />
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={styles.confirmContent}>
          <Box sx={styles.confirmIcon}>
            <Box component="i" className="fa-solid fa-exclamation" />
          </Box>
          <Typography sx={styles.confirmTitle}>
            {approvableCount - approvedCount} pitches still need review
          </Typography>
          <DialogContentText sx={styles.confirmText}>
            You have {approvableCount - approvedCount} unapproved pitches.
            Approving all will lock in the current drafts as-is.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={styles.confirmActions}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Keep reviewing
          </Button>
          <Button
            variant="contained"
            onClick={approveAllAndContinue}
            sx={{ textTransform: "none" }}
          >
            Approve all
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
